import { useMemo } from 'react';
import { useAnalyticsContext } from 'app/modules/analytics/context/AnalyticsContext';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import { useAnalyticsSyncState } from 'app/modules/analytics/components/AnalyticsSyncDriver';
import {
  getAnalyticsContentHeaderIsSyncRecommended,
  getAnalyticsContentHeaderShowReconnectMode,
  getAnalyticsContentHeaderSyncButtonState,
  getAnalyticsContentHeaderSyncTestIds,
  getAnalyticsContentHeaderTrackingDisabledAt,
} from 'app/modules/analytics/utils/analyticsContentHeaderSync';

export function useAnalyticsContentHeaderSync() {
  const { selectedPage, isGroupSelected, enabledGroupPageIds } =
    useAnalyticsContext();
  const { getPageHealth, isLoading: isHealthLoading } =
    useAnalyticsHealthQuery();
  const analyticsSync = useAnalyticsSyncState();

  const selectedPageHealth = useMemo(
    () => (selectedPage != null ? getPageHealth(selectedPage.id) : undefined),
    [getPageHealth, selectedPage],
  );

  const showReconnectMode = useMemo(
    () =>
      getAnalyticsContentHeaderShowReconnectMode(
        isGroupSelected,
        selectedPage,
        selectedPageHealth,
      ),
    [isGroupSelected, selectedPage, selectedPageHealth],
  );

  const isSyncRecommended = useMemo(
    () =>
      getAnalyticsContentHeaderIsSyncRecommended(
        showReconnectMode,
        isGroupSelected,
        enabledGroupPageIds,
        getPageHealth,
        selectedPageHealth,
      ),
    [
      showReconnectMode,
      isGroupSelected,
      enabledGroupPageIds,
      getPageHealth,
      selectedPageHealth,
    ],
  );

  const syncButtonState = useMemo(
    () =>
      getAnalyticsContentHeaderSyncButtonState({
        isShowingSuccess: analyticsSync.isShowingSuccess,
        isSyncing: analyticsSync.isSyncing,
        syncTargetMatchesSelection: analyticsSync.syncTargetMatchesSelection,
        showReconnectMode,
        isHealthLoading,
        selectedPage,
        isGroupSelected,
        enabledGroupPageIds,
        getPageHealth,
        selectedPageHealth,
      }),
    [
      analyticsSync.isShowingSuccess,
      analyticsSync.isSyncing,
      analyticsSync.syncTargetMatchesSelection,
      showReconnectMode,
      isHealthLoading,
      selectedPage,
      isGroupSelected,
      enabledGroupPageIds,
      getPageHealth,
      selectedPageHealth,
    ],
  );

  const trackingDisabledAt = useMemo(
    () =>
      getAnalyticsContentHeaderTrackingDisabledAt(
        isGroupSelected,
        enabledGroupPageIds,
        getPageHealth,
        selectedPageHealth,
      ),
    [isGroupSelected, enabledGroupPageIds, getPageHealth, selectedPageHealth],
  );

  const syncTestIds = useMemo(
    () =>
      getAnalyticsContentHeaderSyncTestIds(syncButtonState, isSyncRecommended),
    [syncButtonState, isSyncRecommended],
  );

  return {
    ...analyticsSync,
    selectedPageHealth,
    showReconnectMode,
    isSyncRecommended,
    syncButtonState,
    syncTestIds,
    trackingDisabledAt,
  };
}
